/**
 * @file React hook — useA1EnergyCalculator.ts: form/state logic for contributor dashboard.
 */

// src/pages/a1/hooks/useA1EnergyCalculator.ts
import { useEffect, useRef, useState, useCallback } from "react";
import { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import axiosClient from "../../../../../../api/axiosClient";

/**
 * Computes total embodied energy (MJ) for A1.
 * Expects values keyed as `embodiedEnergy_extraction_${code}_row${i}` and
 * `embodiedEnergy_unit_extraction_${code}_row${i}`
 */
export function useA1EnergyCalculator(params: {
  formData: FormDataResponse | null;
  values: Record<string, string>;
  sectionRows: Record<string, number>;
}) {
  const { formData, values, sectionRows } = params;
  const [energySummaryMj, setEnergySummaryMj] = useState<number>(0);
  const unitTokenByIdRef = useRef<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get("/units");
        const raw = res?.data?.data ?? res?.data;

        const byId: Record<string, string> = {};
        const normalize = (s: unknown) =>
          String(s ?? "").trim().replace(/\s+/g, "");

        const pickToken = (u: any) => {
          const sym = normalize(u?.symbol || u?.unitSymbol || "");
          if (sym) return sym;
          const name = normalize(u?.name || u?.displayName || "");
          return name;
        };

        const consumeUnit = (u: any) => {
          if (!u?.id) return;
          const token = pickToken(u);
          if (!token) return;
          byId[String(u.id)] = token;
        };

        if (Array.isArray(raw)) {
          raw.forEach((propertyOrUnit: any) => {
            if (Array.isArray(propertyOrUnit?.units)) {
              propertyOrUnit.units.forEach(consumeUnit);
            } else {
              consumeUnit(propertyOrUnit);
            }
          });
        } else if (raw && Array.isArray((raw as any).units)) {
          (raw as any).units.forEach(consumeUnit);
        }

        if (!cancelled) unitTokenByIdRef.current = byId;
      } catch {
        // ignore; we can still compute when unit tokens are present
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const computeAndSetEnergySummary = useCallback(() => {
    if (!formData) {
      setEnergySummaryMj(0);
      return 0;
    }

    const energySec = (formData.formSubSections || []).find(
      (s) => (s.code || s.name) === "A1_EMBODIED_ENERGY"
    );
    if (!energySec) {
      setEnergySummaryMj(0);
      return 0;
    }

    const code = energySec.code || energySec.name || String(energySec.id);
    const rows = sectionRows[code] || 1;

    let totalMj = 0;
    for (let i = 0; i < rows; i++) {
      const rowKey = `row${i}`;
      const valRaw = values[`embodiedEnergy_extraction_${code}_${rowKey}`];
      const unitRaw = values[`embodiedEnergy_unit_extraction_${code}_${rowKey}`];

      const num = Number(valRaw || "0");
      if (!num) continue;
      const unitStr = (unitRaw || "").toString();
      const token =
        /^[0-9]+$/.test(unitStr) ? unitTokenByIdRef.current[unitStr] || "" : unitStr;
      if (token === "MJ") totalMj += num;
      else if (token === "kWh") totalMj += num * 3.6;
    }

    const rounded = Math.round(totalMj * 1000) / 1000;
    setEnergySummaryMj(rounded);
    return rounded;
  }, [formData, values, sectionRows]);

  useEffect(() => {
    computeAndSetEnergySummary();
  }, [computeAndSetEnergySummary, values, sectionRows, formData]);

  return { energySummaryMj, computeAndSetEnergySummary };
}
